<script setup lang="ts">
import HasPermission from "@/components/HasPermission.vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { watch } from "vue";
import useGroupUsers from "../composables/useGroupUsers";

interface Props {
  openModalUsers: boolean;
  groupId: number | string;
  groupName: string;
}

const emits = defineEmits(["openModalUsers"]);
const props = defineProps<Props>();

const { groupUsers, refetch, isLoading } = useGroupUsers({
  params: {
    groupId: props.groupId,
  },
  immediate: false,
});

watch(
  () => props.openModalUsers,
  (value) => {
    if (value) refetch();
  },
);

const getInitials = (user: { name: string; lastname: string }) => {
  return `${user.name?.[0] ?? ''}${user.lastname?.[0] ?? ''}`.toUpperCase()
}

const avatarColors = [
  'bg-blue-600', 'bg-indigo-600', 'bg-violet-600',
  'bg-teal-600', 'bg-emerald-600', 'bg-slate-600',
]

const getAvatarColor = (index: number) => avatarColors[index % avatarColors.length]
</script>

<template>
  <div>
    <TransitionRoot appear :show="openModalUsers" as="template">
      <Dialog as="div" class="relative z-50" @close="emits('openModalUsers')">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex items-center justify-center min-h-full p-4">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel class="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
                <!-- Header -->
                <div class="bg-[#021F58] px-6 py-5 flex items-center justify-between">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                      <span class="material-symbols-outlined text-white text-xl">group</span>
                    </div>
                    <div>
                      <p class="text-xs text-white/60 font-medium uppercase tracking-widest">Integrantes del grupo</p>
                      <DialogTitle as="h3" class="text-white font-bold text-lg leading-tight">
                        {{ groupName }}
                      </DialogTitle>
                    </div>
                  </div>
                  <button
                    @click="emits('openModalUsers')"
                    class="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-colors"
                  >
                    <span class="material-symbols-outlined text-white text-lg">close</span>
                  </button>
                </div>

                <!-- Stats bar -->
                <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                  <div class="flex items-center gap-2">
                    <span class="w-7 h-7 bg-[#021F58]/10 rounded-lg flex items-center justify-center">
                      <span class="material-symbols-outlined text-[#021F58] text-sm">people</span>
                    </span>
                    <span class="text-sm font-semibold text-gray-700">
                      {{ groupUsers?.length ?? 0 }} integrantes
                    </span>
                  </div>
                  <HasPermission name-permission="view-policies" :entity-type="'App\\Models\\CompanyGroup'">
                    <RouterLink
                      class="inline-flex items-center gap-1.5 px-4 py-2 bg-[#021F58] hover:bg-[#1C3F7A] text-white text-xs font-semibold rounded-lg transition-colors"
                      :to="{ name: 'groups.show.id', params: { id: props.groupId } }"
                    >
                      <span class="material-symbols-outlined text-sm">person_add</span>
                      Agregar integrante
                    </RouterLink>
                  </HasPermission>
                </div>

                <!-- Body -->
                <div class="px-6 py-4 max-h-[55vh] overflow-y-auto">
                  <div v-if="!isLoading">
                    <ul class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <li
                        v-for="(user, index) in groupUsers"
                        :key="user.id"
                        class="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-100 hover:border-[#021F58]/20 hover:bg-gray-50 transition-all"
                      >
                        <div
                          class="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                          :class="getAvatarColor(index)"
                        >
                          <span class="text-white text-xs font-bold">{{ getInitials(user) }}</span>
                        </div>
                        <span class="text-sm text-gray-800 font-medium truncate">
                          {{ user.name }} {{ user.lastname }}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div v-else class="flex flex-col items-center justify-center py-16 gap-3">
                    <div class="w-10 h-10 border-[3px] border-[#021F58]/20 border-t-[#021F58] rounded-full animate-spin"></div>
                    <p class="text-sm text-gray-500">Cargando integrantes...</p>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>
